import { useState } from "react";
import { ChecklistStep } from "../../components/widgets/ProgressBar";
import TextInput from "../../components/forms/TextInput";
import SelectInput from "../../components/forms/SelectInput";
import ChoiceGroup from "../../components/forms/ChoiceGroup";
import CheckboxGroup from "../../components/forms/CheckboxGroup";
import SliderInput from "../../components/forms/SliderInput";
import NotesField from "../../components/forms/NotesField";
import { useStore } from "../../js/state/store";
import { REGIONS, US_STATES } from "../../js/utils/checklistOptions";

function minutesLabel(value) {
  const amount = Number(value);
  return amount >= 90 ? "90+ minutes" : `${amount} minutes`;
}

export default function BuyerLocation() {
  const saved = useStore().state.buyer.current.preferences.location || {};
  const [form, setForm] = useState({
    region: saved.region || "",
    state: saved.state || "",
    county: saved.county || "",
    city: saved.city || "",
    setting: saved.setting || "",
    commuteMode: saved.commuteMode || [],
    commuteMinutes: saved.commuteMinutes ?? 30,
    climate: saved.climate || [],
    noise: saved.noise || "",
    proximityWork: saved.proximityWork ?? 30,
    proximityFamily: saved.proximityFamily ?? 45,
    proximityAirport: saved.proximityAirport ?? 45,
    proximityShopping: saved.proximityShopping ?? 15,
    proximityRecreation: saved.proximityRecreation ?? 20,
    notes: saved.notes || "",
  });

  function update(event) {
    const { name, value, type } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "range" ? Number(value) : value,
    }));
  }

  return (
    <ChecklistStep
      title="Location"
      lead="Tell your agent where life should be centered — region, setting, commute, climate, and how close you need to be to everyday places."
      preferenceKey="location"
      value={form}
    >
      <section className="checklist-section">
        <h2>Region & Place</h2>
        <p className="section-help">Start wide, then name the state, county, and city you want searched first.</p>
        <SelectInput label="Region" name="region" value={form.region} onChange={update} options={REGIONS} />
        <div className="two-col">
          <SelectInput label="State" name="state" value={form.state} onChange={update} options={US_STATES} />
          <TextInput label="County" name="county" value={form.county} onChange={update} placeholder="County name" />
        </div>
        <TextInput label="City / community" name="city" value={form.city} onChange={update} placeholder="Primary city or search area" />
        <ChoiceGroup
          label="Setting"
          name="setting"
          value={form.setting}
          onChange={update}
          options={[
            { value: "urban", label: "Urban" },
            { value: "suburban", label: "Suburban" },
            { value: "rural", label: "Rural" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Commute</h2>
        <CheckboxGroup
          label="How you expect to get around"
          name="commuteMode"
          values={form.commuteMode}
          onChange={update}
          layout="grid"
          options={[
            { value: "car", label: "Drive" },
            { value: "transit", label: "Public transit" },
            { value: "walk-bike", label: "Walk or bike" },
            { value: "hybrid", label: "Mix / hybrid" },
          ]}
        />
        <SliderInput
          label="Maximum commute time"
          name="commuteMinutes"
          min={5}
          max={90}
          step={5}
          value={form.commuteMinutes}
          onChange={update}
          formatValue={minutesLabel}
        />
      </section>

      <section className="checklist-section">
        <h2>Climate & Noise</h2>
        <CheckboxGroup
          label="Climate preferences"
          name="climate"
          values={form.climate}
          onChange={update}
          layout="grid"
          options={[
            { value: "warm", label: "Warm year-round" },
            { value: "mild", label: "Mild / Mediterranean" },
            { value: "four-seasons", label: "Four seasons" },
            { value: "dry", label: "Dry / arid" },
            { value: "humid", label: "Okay with humidity" },
            { value: "snow", label: "Okay with snow" },
          ]}
        />
        <ChoiceGroup
          label="Noise tolerance"
          name="noise"
          value={form.noise}
          onChange={update}
          hint="Think about traffic, nightlife, airports, and neighbors — not who lives nearby."
          options={[
            { value: "quiet", label: "Very quiet" },
            { value: "moderate", label: "Moderate" },
            { value: "lively", label: "Lively / city energy" },
          ]}
        />
      </section>

      <section className="checklist-section">
        <h2>Proximity</h2>
        <p className="section-help">How close is close enough? Slide each one to a comfortable drive time.</p>
        <SliderInput label="Work / school" name="proximityWork" min={5} max={90} step={5} value={form.proximityWork} onChange={update} formatValue={minutesLabel} />
        <SliderInput label="Family / frequent visits" name="proximityFamily" min={5} max={90} step={5} value={form.proximityFamily} onChange={update} formatValue={minutesLabel} />
        <SliderInput label="Airport" name="proximityAirport" min={5} max={90} step={5} value={form.proximityAirport} onChange={update} formatValue={minutesLabel} />
        <SliderInput label="Shopping & daily errands" name="proximityShopping" min={5} max={90} step={5} value={form.proximityShopping} onChange={update} formatValue={minutesLabel} />
        <SliderInput label="Recreation & outdoors" name="proximityRecreation" min={5} max={90} step={5} value={form.proximityRecreation} onChange={update} formatValue={minutesLabel} />
      </section>

      <section className="checklist-section">
        <NotesField
          label="Location Notes for My agent"
          name="notes"
          value={form.notes}
          onChange={update}
          placeholder="Areas to include or skip, flood or fire concerns, HOA towns vs. unincorporated, and anything else about place."
        />
      </section>
    </ChecklistStep>
  );
}
